import { useState } from 'react';
import { Mail, ArrowLeft, CheckCircle, ShieldCheck, AlertCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { Link } from '../components/Router';

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const inputStyle = "w-full pl-11 pr-4 py-3 bg-gray-100 border-0 rounded-2xl text-gray-900 placeholder-gray-500 focus:bg-white focus:ring-2 focus:ring-[#D5462E]/10 transition-all outline-none font-medium";

  const handleResetRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const siteUrl = import.meta.env.VITE_SITE_URL || window.location.origin;

      const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${siteUrl}/reset-password`,
      });

      if (resetError) throw resetError;

      setSuccess(true);
    } catch (err: any) {
      setError(err.message || 'Erreur lors de l\'envoi de l\'email');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-white">
      <div className="w-full lg:w-1/2 flex flex-col justify-center px-4 sm:px-6 md:px-8 lg:px-24 relative py-8 lg:py-0">
        <div className="absolute top-8 left-8 z-10">
          <Link 
            to="/connecter" 
            className="flex items-center text-sm font-semibold text-gray-500 hover:text-[#D5462E] transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Retour
          </Link>
        </div>

        <div className="max-w-md w-full mx-auto">
          <div className="mb-8">
            <h1 className="text-2xl font-semibold text-gray-900 tracking-tight mb-2">Mot de passe oublié</h1>
            <p className="text-gray-500 text-base">Entrez votre email pour recevoir un lien de réinitialisation.</p>
          </div>

          {success ? (
            <div className="bg-green-50/50 backdrop-blur-xl border border-green-100 rounded-3xl p-8 text-center">
              <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-7 w-7 text-green-600" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Email envoyé!</h2>
              <p className="text-gray-600 mb-5 text-sm leading-relaxed">
                Un lien de réinitialisation a été envoyé à <strong>{email}</strong>.<br/>
                Veuillez vérifier votre boîte de réception.
              </p>
              <div className="bg-amber-50/50 backdrop-blur-xl border border-amber-100 rounded-2xl p-4 mb-4">
                <div className="flex items-start">
                  <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 mr-3 flex-shrink-0" />
                  <div className="text-sm text-amber-800 text-left">
                    <p className="font-semibold mb-1">N'oubliez pas:</p>
                    <ul className="list-disc list-inside space-y-1 text-sm">
                      <li>Vérifiez aussi vos spams</li>
                      <li>Le lien expire dans 1 heure</li>
                    </ul>
                  </div>
                </div>
              </div>
              <Link
                to="/connecter"
                className="inline-flex items-center justify-center w-full bg-[#D5462E] text-white py-3 rounded-full font-semibold hover:bg-[#b83a24] transition-all"
              >
                Retour à la connexion
              </Link>
            </div>
          ) : (
            <>
              {error && (
                <div className="mb-5 p-4 bg-red-50/50 backdrop-blur-xl border border-red-100 rounded-2xl flex items-start">
                  <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
                  <p className="text-sm text-red-600 font-medium">{error}</p>
                </div>
              )}

              <form onSubmit={handleResetRequest} className="space-y-5">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Adresse Email</label>
                  <div className="relative group">
                    <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-[#D5462E] transition-colors">
                      <Mail className="h-5 w-5" />
                    </div>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className={inputStyle}
                      placeholder="exemple@email.com"
                      required
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#D5462E] text-white py-3 rounded-full font-semibold hover:bg-[#b83a24] active:transform active:scale-[0.98] transition-all disabled:opacity-70 disabled:cursor-not-allowed mt-2"
                >
                  {loading ? (
                    <div className="flex items-center justify-center">
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                      Envoi...
                    </div>
                  ) : (
                    'Envoyer le lien'
                  )}
                </button>
              </form>

              <div className="mt-8 pt-8 border-t border-gray-100 text-center">
                <Link to="/connecter" className="text-sm font-semibold text-gray-500 hover:text-[#D5462E] hover:underline">
                  Annuler et se connecter
                </Link>
                
                <div className="mt-5 flex items-center justify-center space-x-2 text-xs text-gray-400">
                  <ShieldCheck className="h-3.5 w-3.5" />
                  <span>Processus sécurisé</span>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="hidden lg:block lg:w-1/2 relative bg-gray-900">
        <div className="absolute inset-0 bg-gradient-to-bl from-[#D5462E]/30 to-black/70 z-10" />
        <img 
          src="https://i.ibb.co/jvwCLsJ1/photo2.png" 
          alt="Car Security" 
          className="absolute inset-0 w-full h-full object-cover opacity-90"
        />
      </div>
    </div>
  );
}